import React, { useState, useRef, useEffect, useCallback } from 'react';

const CROP_TYPES = {
  image: { aspectRatio: 1, label: 'Square (1:1)', color: 'blue' },
  promo: { aspectRatio: 16/9, label: 'TV/Promo (16:9)', color: 'green' },
  strip: { aspectRatio: 4/3, label: 'Standard (4:3)', color: 'purple' }
};

const POSITIONS = {
  'top-left': { x: 0, y: 0 },
  'top-center': { x: 0.5, y: 0 },
  'top-right': { x: 1, y: 0 },
  'center-left': { x: 0, y: 0.5 },
  'center': { x: 0.5, y: 0.5 },
  'center-right': { x: 1, y: 0.5 },
  'bottom-left': { x: 0, y: 1 },
  'bottom-center': { x: 0.5, y: 1 },
  'bottom-right': { x: 1, y: 1 }
};

function ImageCropEditor(props) {
  // Parse props - handle both camelCase and kebab-case from remount
  const imageSrc = props.imageSrc || props['image-src'] || null;
  const fieldPrefix = props.fieldPrefix || props['field-prefix'] || 'event';
  const originalWidth = parseInt(props.originalWidth || props['original-width'] || 0) || 0;
  const originalHeight = parseInt(props.originalHeight || props['original-height'] || 0) || 0;
  
  // Parse initial crop data
  const initialCropData = props.cropData || props['crop-data'] || '{}';
  const parsedCropData = typeof initialCropData === 'string' ? JSON.parse(initialCropData) : initialCropData;
  
  // State
  const [activeCropType, setActiveCropType] = useState('image');
  const [cropData, setCropData] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [activeHandle, setActiveHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectionStart, setSelectionStart] = useState(null);
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0, scale: 1 });
  
  // Refs
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  
  // Calculate image dimensions and scale
  useEffect(() => {
    if (!imgRef.current || !originalWidth || !originalHeight) return;
    
    const updateDimensions = () => {
      const img = imgRef.current;
      const displayWidth = img.clientWidth;
      const displayHeight = img.clientHeight;
      const scale = originalWidth > 0 ? displayWidth / originalWidth : 1;
      
      setImgDimensions({
        width: displayWidth,
        height: displayHeight,
        scale: scale || 1
      });
    };
    
    // Update on image load and window resize
    if (imgRef.current.complete) {
      updateDimensions();
    } else {
      imgRef.current.addEventListener('load', updateDimensions);
    }
    
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [originalWidth, originalHeight]);
  
  // Convert image coordinates to display coordinates
  const imageToDisplay = useCallback((coords) => {
    if (!coords) return null;
    return {
      x1: coords.x1 * imgDimensions.scale,
      y1: coords.y1 * imgDimensions.scale,
      x2: coords.x2 * imgDimensions.scale,
      y2: coords.y2 * imgDimensions.scale
    };
  }, [imgDimensions.scale]);
  
  // Convert display coordinates to image coordinates
  const displayToImage = useCallback((coords) => {
    return {
      x1: Math.round(coords.x1 / imgDimensions.scale),
      y1: Math.round(coords.y1 / imgDimensions.scale),
      x2: Math.round(coords.x2 / imgDimensions.scale),
      y2: Math.round(coords.y2 / imgDimensions.scale)
    };
  }, [imgDimensions.scale]);
  
  // Update hidden form fields
  const updateFormFields = useCallback((cropType, coords) => {
    ['x1', 'y1', 'x2', 'y2'].forEach(coord => {
      const fieldId = `${fieldPrefix}_${cropType}_thumbnail_${coord}`;
      const field = document.getElementById(fieldId);
      if (field) {
        // Use toString to ensure 0 values are preserved
        field.value = coords[coord] !== null && coords[coord] !== undefined ? coords[coord].toString() : '';
      }
    });
  }, [fieldPrefix]);
  
  // Create default crop for a type
  const createDefaultCrop = useCallback((cropType) => {
    const { aspectRatio } = CROP_TYPES[cropType];
    const maxWidth = originalWidth * 0.8;
    const maxHeight = originalHeight * 0.8;
    
    let width, height;
    if (maxWidth / maxHeight > aspectRatio) {
      height = maxHeight;
      width = height * aspectRatio;
    } else {
      width = maxWidth;
      height = width / aspectRatio;
    }
    
    const x1 = Math.round((originalWidth - width) / 2);
    const y1 = Math.round((originalHeight - height) / 2);
    const x2 = Math.round(x1 + width);
    const y2 = Math.round(y1 + height);
    
    return { x1, y1, x2, y2 };
  }, [originalWidth, originalHeight]);
  
  // Constrain crop to image bounds
  const constrainToImage = useCallback((coords) => {
    // First ensure x2 > x1 and y2 > y1
    let { x1, y1, x2, y2 } = coords;
    if (x1 > x2) [x1, x2] = [x2, x1];
    if (y1 > y2) [y1, y2] = [y2, y1];
    
    // Constrain to image bounds
    x1 = Math.max(0, Math.min(x1, originalWidth));
    y1 = Math.max(0, Math.min(y1, originalHeight));
    x2 = Math.max(0, Math.min(x2, originalWidth));
    y2 = Math.max(0, Math.min(y2, originalHeight));
    
    // Ensure minimum size of 10px
    const minSize = 10;
    if (x2 - x1 < minSize) {
      if (x1 === 0) {
        x2 = Math.min(x1 + minSize, originalWidth);
      } else if (x2 === originalWidth) {
        x1 = Math.max(x2 - minSize, 0);
      } else {
        const center = (x1 + x2) / 2;
        x1 = Math.max(0, center - minSize / 2);
        x2 = Math.min(originalWidth, center + minSize / 2);
      }
    }
    if (y2 - y1 < minSize) {
      if (y1 === 0) {
        y2 = Math.min(y1 + minSize, originalHeight);
      } else if (y2 === originalHeight) {
        y1 = Math.max(y2 - minSize, 0);
      } else {
        const center = (y1 + y2) / 2;
        y1 = Math.max(0, center - minSize / 2);
        y2 = Math.min(originalHeight, center + minSize / 2);
      }
    }
    
    return { x1, y1, x2, y2 };
  }, [originalWidth, originalHeight]);
  
  // Handle quick position buttons
  const setQuickPosition = useCallback((position) => {
    const pos = POSITIONS[position];
    const currentCrop = cropData[activeCropType] || createDefaultCrop(activeCropType);
    const width = currentCrop.x2 - currentCrop.x1;
    const height = currentCrop.y2 - currentCrop.y1;
    
    let x1, y1;
    if (pos.x === 0) x1 = 0;
    else if (pos.x === 0.5) x1 = (originalWidth - width) / 2;
    else x1 = originalWidth - width;
    
    if (pos.y === 0) y1 = 0;
    else if (pos.y === 0.5) y1 = (originalHeight - height) / 2;
    else y1 = originalHeight - height;
    
    const newCrop = constrainToImage({
      x1: Math.round(x1),
      y1: Math.round(y1),
      x2: Math.round(x1 + width),
      y2: Math.round(y1 + height)
    });
    
    setCropData(prev => ({ ...prev, [activeCropType]: newCrop }));
    updateFormFields(activeCropType, newCrop);
  }, [activeCropType, cropData, originalWidth, originalHeight, createDefaultCrop, constrainToImage, updateFormFields]);
  
  // Mouse event handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Check if clicking on a handle
    if (e.target.dataset.handle) {
      setIsResizing(true);
      setActiveHandle(e.target.dataset.handle);
      setDragStart({ x: e.clientX, y: e.clientY });
      setSelectionStart(cropData[activeCropType]);
    }
    // Check if clicking inside selection
    else if (e.target.closest('.crop-selection')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setSelectionStart(cropData[activeCropType]);
    }
    // Start new selection
    else {
      const imageCoords = displayToImage({ x1: x, y1: y, x2: x, y2: y });
      setIsResizing(true);
      setActiveHandle('se');
      setDragStart({ x: e.clientX, y: e.clientY });
      setSelectionStart(imageCoords);
      setCropData(prev => ({ ...prev, [activeCropType]: imageCoords }));
    }
  }, [activeCropType, cropData, displayToImage]);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;
    
    const deltaX = (e.clientX - dragStart.x) / imgDimensions.scale;
    const deltaY = (e.clientY - dragStart.y) / imgDimensions.scale;
    
    let newCrop;
    
    if (isDragging) {
      // Move entire selection
      const width = selectionStart.x2 - selectionStart.x1;
      const height = selectionStart.y2 - selectionStart.y1;
      
      let newX1 = selectionStart.x1 + deltaX;
      let newY1 = selectionStart.y1 + deltaY;
      
      // Constrain movement to keep crop inside image
      newX1 = Math.max(0, Math.min(newX1, originalWidth - width));
      newY1 = Math.max(0, Math.min(newY1, originalHeight - height));
      
      newCrop = {
        x1: newX1,
        y1: newY1,
        x2: newX1 + width,
        y2: newY1 + height
      };
    } else if (isResizing) {
      // Resize based on active handle
      const { aspectRatio } = CROP_TYPES[activeCropType];
      newCrop = { ...selectionStart };
      
      // First apply the drag delta, then constrain while maintaining aspect ratio
      switch (activeHandle) {
        case 'se':
          // Southeast corner - anchor northwest
          newCrop.x2 = selectionStart.x2 + deltaX;
          newCrop.y2 = selectionStart.y2 + deltaY;
          // Constrain to bounds first
          newCrop.x2 = Math.min(originalWidth, Math.max(selectionStart.x1 + 10, newCrop.x2));
          newCrop.y2 = Math.min(originalHeight, Math.max(selectionStart.y1 + 10, newCrop.y2));
          // Adjust to maintain aspect ratio
          const widthSE = newCrop.x2 - newCrop.x1;
          const heightSE = widthSE / aspectRatio;
          if (newCrop.y1 + heightSE > originalHeight) {
            // Constrained by height
            newCrop.y2 = originalHeight;
            newCrop.x2 = newCrop.x1 + (newCrop.y2 - newCrop.y1) * aspectRatio;
          } else {
            newCrop.y2 = newCrop.y1 + heightSE;
          }
          break;
          
        case 'sw':
          // Southwest corner - anchor northeast
          newCrop.x1 = selectionStart.x1 + deltaX;
          newCrop.y2 = selectionStart.y2 + deltaY;
          // Constrain to bounds
          newCrop.x1 = Math.max(0, Math.min(selectionStart.x2 - 10, newCrop.x1));
          newCrop.y2 = Math.min(originalHeight, Math.max(selectionStart.y1 + 10, newCrop.y2));
          // Adjust to maintain aspect ratio
          const widthSW = newCrop.x2 - newCrop.x1;
          const heightSW = widthSW / aspectRatio;
          if (newCrop.y1 + heightSW > originalHeight) {
            newCrop.y2 = originalHeight;
            newCrop.x1 = newCrop.x2 - (newCrop.y2 - newCrop.y1) * aspectRatio;
          } else {
            newCrop.y2 = newCrop.y1 + heightSW;
          }
          break;
          
        case 'ne':
          // Northeast corner - anchor southwest
          newCrop.x2 = selectionStart.x2 + deltaX;
          newCrop.y1 = selectionStart.y1 + deltaY;
          // Constrain to bounds
          newCrop.x2 = Math.min(originalWidth, Math.max(selectionStart.x1 + 10, newCrop.x2));
          newCrop.y1 = Math.max(0, Math.min(selectionStart.y2 - 10, newCrop.y1));
          // Adjust to maintain aspect ratio
          const widthNE = newCrop.x2 - newCrop.x1;
          const heightNE = widthNE / aspectRatio;
          if (newCrop.y2 - heightNE < 0) {
            newCrop.y1 = 0;
            newCrop.x2 = newCrop.x1 + (newCrop.y2 - newCrop.y1) * aspectRatio;
          } else {
            newCrop.y1 = newCrop.y2 - heightNE;
          }
          break;
          
        case 'nw':
          // Northwest corner - anchor southeast
          newCrop.x1 = selectionStart.x1 + deltaX;
          newCrop.y1 = selectionStart.y1 + deltaY;
          // Constrain to bounds
          newCrop.x1 = Math.max(0, Math.min(selectionStart.x2 - 10, newCrop.x1));
          newCrop.y1 = Math.max(0, Math.min(selectionStart.y2 - 10, newCrop.y1));
          // Adjust to maintain aspect ratio
          const widthNW = newCrop.x2 - newCrop.x1;
          const heightNW = widthNW / aspectRatio;
          if (newCrop.y2 - heightNW < 0) {
            newCrop.y1 = 0;
            newCrop.x1 = newCrop.x2 - (newCrop.y2 - newCrop.y1) * aspectRatio;
          } else {
            newCrop.y1 = newCrop.y2 - heightNW;
          }
          break;
          
        case 'n':
        case 's':
        case 'e':
        case 'w':
          // For edge handles, resize from center
          const centerX = (selectionStart.x1 + selectionStart.x2) / 2;
          const centerY = (selectionStart.y1 + selectionStart.y2) / 2;
          
          if (activeHandle === 'n' || activeHandle === 's') {
            // North/South - change height, adjust width
            if (activeHandle === 'n') {
              newCrop.y1 = Math.max(0, Math.min(selectionStart.y2 - 10, selectionStart.y1 + deltaY));
            } else {
              newCrop.y2 = Math.min(originalHeight, Math.max(selectionStart.y1 + 10, selectionStart.y2 + deltaY));
            }
            const height = newCrop.y2 - newCrop.y1;
            const width = height * aspectRatio;
            newCrop.x1 = Math.max(0, centerX - width / 2);
            newCrop.x2 = Math.min(originalWidth, centerX + width / 2);
            // If constrained by width, adjust height
            if (newCrop.x2 - newCrop.x1 < width) {
              const constrainedWidth = newCrop.x2 - newCrop.x1;
              const constrainedHeight = constrainedWidth / aspectRatio;
              if (activeHandle === 'n') {
                newCrop.y1 = newCrop.y2 - constrainedHeight;
              } else {
                newCrop.y2 = newCrop.y1 + constrainedHeight;
              }
            }
          } else {
            // East/West - change width, adjust height
            if (activeHandle === 'w') {
              newCrop.x1 = Math.max(0, Math.min(selectionStart.x2 - 10, selectionStart.x1 + deltaX));
            } else {
              newCrop.x2 = Math.min(originalWidth, Math.max(selectionStart.x1 + 10, selectionStart.x2 + deltaX));
            }
            const width = newCrop.x2 - newCrop.x1;
            const height = width / aspectRatio;
            newCrop.y1 = Math.max(0, centerY - height / 2);
            newCrop.y2 = Math.min(originalHeight, centerY + height / 2);
            // If constrained by height, adjust width
            if (newCrop.y2 - newCrop.y1 < height) {
              const constrainedHeight = newCrop.y2 - newCrop.y1;
              const constrainedWidth = constrainedHeight * aspectRatio;
              if (activeHandle === 'w') {
                newCrop.x1 = newCrop.x2 - constrainedWidth;
              } else {
                newCrop.x2 = newCrop.x1 + constrainedWidth;
              }
            }
          }
          break;
      }
    }
    
    // Round coordinates first
    newCrop = {
      x1: Math.round(newCrop.x1),
      y1: Math.round(newCrop.y1),
      x2: Math.round(newCrop.x2),
      y2: Math.round(newCrop.y2)
    };
    
    // Then constrain to image bounds
    newCrop = constrainToImage(newCrop);
    
    setCropData(prev => ({ ...prev, [activeCropType]: newCrop }));
    updateFormFields(activeCropType, newCrop);
  }, [isDragging, isResizing, dragStart, selectionStart, imgDimensions.scale, activeCropType, activeHandle, constrainToImage, updateFormFields, originalWidth, originalHeight]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setActiveHandle(null);
    setSelectionStart(null);
  }, []);
  
  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);
  
  // Initialize crops on mount
  useEffect(() => {
    if (!originalWidth || !originalHeight) return;
    
    const newCropData = {};
    let hasAnyCrops = false;
    
    // First pass - check if we have any valid crop data
    Object.keys(CROP_TYPES).forEach(cropType => {
      const crop = parsedCropData[cropType];
      if (crop && (crop.x1 !== null && crop.x1 !== undefined || 
                   crop.y1 !== null && crop.y1 !== undefined || 
                   crop.x2 !== null && crop.x2 !== undefined || 
                   crop.y2 !== null && crop.y2 !== undefined)) {
        // Convert any string values to numbers - don't use || 0 as it treats 0 as falsy!
        const validCrop = {
          x1: crop.x1 !== null && crop.x1 !== undefined ? parseInt(crop.x1) : 0,
          y1: crop.y1 !== null && crop.y1 !== undefined ? parseInt(crop.y1) : 0,
          x2: crop.x2 !== null && crop.x2 !== undefined ? parseInt(crop.x2) : 0,
          y2: crop.y2 !== null && crop.y2 !== undefined ? parseInt(crop.y2) : 0
        };
        
        // Only use if we have a valid crop area
        if (validCrop.x2 > validCrop.x1 && validCrop.y2 > validCrop.y1) {
          newCropData[cropType] = validCrop;
          hasAnyCrops = true;
        }
      }
    });
    
    // Second pass - create defaults for missing crops
    Object.keys(CROP_TYPES).forEach(cropType => {
      if (!newCropData[cropType]) {
        const defaultCrop = createDefaultCrop(cropType);
        newCropData[cropType] = defaultCrop;
      }
      // Update form fields with the final crop data
      updateFormFields(cropType, newCropData[cropType]);
    });
    
    setCropData(newCropData);
  }, []); // Only run once on mount
  
  const currentCrop = cropData[activeCropType];
  const displayCrop = currentCrop ? imageToDisplay(currentCrop) : null;
  
  // Don't render if no image source or dimensions
  if (!imageSrc || !originalWidth || !originalHeight) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-sm text-yellow-800 dark:text-yellow-200">
          Image crop controls will appear here after saving the event with an image.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Crop type tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {Object.entries(CROP_TYPES).map(([type, config]) => (
            <button
              key={type}
              type="button"
              onClick={() => setActiveCropType(type)}
              className={`
                py-2 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeCropType === type
                  ? type === 'image' ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : type === 'promo' ? 'border-green-500 text-green-600 dark:text-green-400'
                    : 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              {config.label}
            </button>
          ))}
        </nav>
      </div>
      
      {/* Main crop editor */}
      <div className="flex gap-6">
        {/* Left side - Interactive crop area */}
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Adjust crop area
          </h3>
          <div className="relative inline-block">
            <div 
              ref={containerRef}
              className="relative overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-lg"
              onMouseDown={handleMouseDown}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop source"
                className="block max-w-full h-auto select-none"
                draggable={false}
                style={{ maxHeight: '600px' }}
              />
              
              
              {/* Crop overlays for all types */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Show all crop areas with different opacities */}
                {Object.entries(cropData).map(([type, crop]) => {
                  if (!crop || (crop.x1 == null && crop.y1 == null && crop.x2 == null && crop.y2 == null)) return null;
                  const display = imageToDisplay(crop);
                  if (!display) return null;
                  
                  const isActive = type === activeCropType;
                  const { color } = CROP_TYPES[type];
                  
                  return (
                    <div
                      key={type}
                      className={`absolute border-2 transition-all ${
                        isActive ? 'opacity-100' : 'opacity-50'
                      } ${
                        type === 'image' ? 'border-blue-500 shadow-blue-500/50' :
                        type === 'promo' ? 'border-green-500 shadow-green-500/50' :
                        'border-purple-500 shadow-purple-500/50'
                      } ${isActive ? 'shadow-lg' : ''}`}
                      style={{
                        left: display.x1,
                        top: display.y1,
                        width: display.x2 - display.x1,
                        height: display.y2 - display.y1,
                        pointerEvents: isActive ? 'auto' : 'none'
                      }}
                    >
                      {/* Only show interactive elements for active crop - no fill */}
                      {isActive && (
                        <>
                          <div className="absolute inset-0 cursor-move crop-selection" />
                          
                          {/* Grid lines */}
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute top-1/3 left-0 right-0 h-px bg-white bg-opacity-50" />
                            <div className="absolute top-2/3 left-0 right-0 h-px bg-white bg-opacity-50" />
                            <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white bg-opacity-50" />
                            <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white bg-opacity-50" />
                          </div>
                          
                          {/* Resize handles */}
                          {['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'].map(handle => (
                            <div
                              key={handle}
                              data-handle={handle}
                              className={`absolute w-3 h-3 bg-white border-2 shadow-sm pointer-events-auto
                                ${type === 'image' ? 'border-blue-500' :
                                  type === 'promo' ? 'border-green-500' :
                                  'border-purple-500'}
                                ${handle.includes('n') ? '-top-1.5' : ''}
                                ${handle.includes('s') ? '-bottom-1.5' : ''}
                                ${handle.includes('w') ? '-left-1.5' : ''}
                                ${handle.includes('e') ? '-right-1.5' : ''}
                                ${handle === 'n' || handle === 's' ? 'left-1/2 -translate-x-1/2' : ''}
                                ${handle === 'e' || handle === 'w' ? 'top-1/2 -translate-y-1/2' : ''}
                                ${handle === 'nw' ? 'cursor-nw-resize' : ''}
                                ${handle === 'n' ? 'cursor-n-resize' : ''}
                                ${handle === 'ne' ? 'cursor-ne-resize' : ''}
                                ${handle === 'e' ? 'cursor-e-resize' : ''}
                                ${handle === 'se' ? 'cursor-se-resize' : ''}
                                ${handle === 's' ? 'cursor-s-resize' : ''}
                                ${handle === 'sw' ? 'cursor-sw-resize' : ''}
                                ${handle === 'w' ? 'cursor-w-resize' : ''}
                              `}
                            />
                          ))}
                        </>
                      )}
                      
                      {/* Label */}
                      <div className={`absolute -top-6 left-0 text-xs font-medium ${
                        isActive ? 'opacity-100' : 'opacity-60'
                      } ${
                        type === 'image' ? 'text-blue-600 dark:text-blue-400' :
                        type === 'promo' ? 'text-green-600 dark:text-green-400' :
                        'text-purple-600 dark:text-purple-400'
                      }`}>
                        {CROP_TYPES[type].label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Right side - Preview */}
        <div className="w-64">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Preview
          </h3>
          <div className="space-y-4">
            {currentCrop && (() => {
              // Calculate preview dimensions
              const cropWidth = currentCrop.x2 - currentCrop.x1;
              const cropHeight = currentCrop.y2 - currentCrop.y1;
              const previewWidth = 200;
              const previewHeight = activeCropType === 'image' ? 200 :
                                   activeCropType === 'promo' ? 112.5 :
                                   150;
              
              // Calculate scale to fit crop in preview
              const scale = previewWidth / cropWidth;
              
              return (
                <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
                  <div className="relative mx-auto overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700" style={{
                    width: `${previewWidth}px`,
                    height: `${previewHeight}px`
                  }}>
                    <img
                      src={imageSrc}
                      alt="Crop preview"
                      style={{
                        position: 'absolute',
                        width: `${originalWidth * scale}px`,
                        height: `${originalHeight * scale}px`,
                        left: `-${currentCrop.x1 * scale}px`,
                        top: `-${currentCrop.y1 * scale}px`,
                        maxWidth: 'none'
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    {cropWidth} × {cropHeight} px
                  </p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div className="space-y-3">
        {/* Quick position buttons */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Quick position:</span>
          {Object.keys(POSITIONS).map(position => (
            <button
              key={position}
              type="button"
              onClick={() => setQuickPosition(position)}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {position.replace('-', ' ')}
            </button>
          ))}
        </div>
        
        {/* Legend */}
        <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 border-2 border-blue-500 rounded"></div>
            <span>Square (1:1)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 border-2 border-green-500 rounded"></div>
            <span>TV/Promo (16:9)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 border-2 border-purple-500 rounded"></div>
            <span>Standard (4:3)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCropEditor;